import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { switchMap, map, distinctUntilChanged, filter } from 'rxjs/operators';
import { ReusableStreamProperty, shallowCompare } from '@ipreo/muni-shared';
import { GameDayViewType } from '../../state/data/state';
import { Tranche } from '../../gameday/data/gameDay-data.transformations';
import { GameDayCalculatedData } from '../../gameday/data/gameDay-data.calculation';
import { SetGamedayViewType } from '../../state/data/actions';
import {
  getGamedayDealState,
  getMonitorState,
  getTrancheOrdersFilteredState,
  getMonitorSelectedMaturityState,
  getMonitorCompanyTypeFilterState,
  getMonitorMemberPriorityFilterState,
  getMonitorRetailPriorityFilterState,
  getMonitorViewTypeState
} from '../../state/data/reducer';
import { GameDayRootReducerState } from '../../state/root.state';
import {
  SetMonitorViewType,
  SetSelectedMaturityId
} from '../../state/monitor/actions';
import {
  filterByCompanyType,
  filterByMemberPriority,
  filterByRetailPriority,
  filteredDataToTranche,
  getFilterAction,
  getFilterFormModel,
  MONITOR_TO_GAME_DAY_VIEW_TYPE_MAPPING
} from '../mappers/monitor.calculations';
import { MonitorViewType, MonitorFilter } from '../models/monitor';
import { CompanyType } from '../models/company';
import { MemberPriority, RetailPriority } from '../models/priority';
import { GameDayModel } from '../../models/gameday-model';

const MONITOR_PRIORITY_ORDER_TYPES: string[] = ['GN', 'ND'];

@Injectable()
export class MonitorService {
  public readonly viewType: Observable<MonitorViewType> = this.store.pipe(
    select(getMonitorViewTypeState),
    distinctUntilChanged()
  );

  public readonly gameDayViewTypeByMonitorViewType: Observable<
    GameDayViewType
  > = this.viewType.pipe(
    map(viewType => MONITOR_TO_GAME_DAY_VIEW_TYPE_MAPPING[viewType]),
    distinctUntilChanged()
  );

  public readonly selectedMaturityId: Observable<string> = this.store.pipe(
    select(getMonitorSelectedMaturityState),
    distinctUntilChanged()
  );

  public readonly filterValues: Observable<MonitorFilter> = this.store.pipe(
    select(getMonitorState),
    map(getFilterFormModel),
    distinctUntilChanged(shallowCompare)
  );

  public readonly isSoleManagedDeal: Observable<boolean> = this.store.pipe(
    select(getGamedayDealState),
    map((deal: GameDayModel.Deal) => !!deal && deal.syndicate.length === 1),
    distinctUntilChanged()
  );

  @ReusableStreamProperty
  public readonly filterByCompanyType$: Observable<
    CompanyType
  > = this.store.pipe(
    select(getMonitorCompanyTypeFilterState),
    distinctUntilChanged()
  );

  @ReusableStreamProperty
  public readonly filterByMemberPriority$: Observable<
    MemberPriority
  > = this.store.pipe(
    select(getMonitorMemberPriorityFilterState),
    distinctUntilChanged()
  );

  @ReusableStreamProperty
  public readonly filterByRetailPriority$: Observable<
    RetailPriority
  > = this.store.pipe(
    select(getMonitorRetailPriorityFilterState),
    distinctUntilChanged()
  );

  @ReusableStreamProperty
  public readonly data: Observable<GameDayCalculatedData> = combineLatest(
    this.store.pipe(
      select(getTrancheOrdersFilteredState),
      filter(({ orders, trancheGroups }) => !!orders && !!trancheGroups)
    ),
    this.filterByCompanyType$,
    this.filterByMemberPriority$,
    this.filterByRetailPriority$
  ).pipe(
    map(([filteredData, companyType, memberPriority, retailPriority]) => {
      const companyTypeFilter = filterByCompanyType(filteredData, companyType);
      const memberPriorityFilter = filterByMemberPriority(
        companyTypeFilter,
        memberPriority
      );
      const retailPriorityFilter = filterByRetailPriority(
        memberPriorityFilter,
        retailPriority
      );

      return retailPriorityFilter;
    }),
    distinctUntilChanged()
  );

  @ReusableStreamProperty
  public readonly retailData = this.data.pipe(
    map((data: GameDayCalculatedData) => {
      const orders = data.orders.filter(
        order => order.priority === GameDayModel.OrderPriority.retail
      );
      return {
        ...data,
        orders
      };
    }),
    distinctUntilChanged()
  );

  @ReusableStreamProperty
  public readonly priorityData = this.data.pipe(
    map(data => {
      const orders = data.orders.filter(
        order => MONITOR_PRIORITY_ORDER_TYPES.indexOf(order.orderType.code) > -1
      );
      return {
        ...data,
        orders
      };
    }),
    distinctUntilChanged()
  );

  private readonly dataMapByViewType: {
    [key: string]: Observable<GameDayCalculatedData>;
  } = {
    [MonitorViewType.Overview]: this.data,
    [MonitorViewType.Retail]: this.retailData,
    [MonitorViewType.Priority]: this.priorityData
  };

  constructor(private store: Store<GameDayRootReducerState>) {}

  public setViewType(viewType: MonitorViewType): void {
    this.store.dispatch(new SetMonitorViewType(viewType));
  }

  public selectMaturity(id: string): void {
    this.store.dispatch(new SetSelectedMaturityId(id));
  }

  public setFilter(monitorFilter: MonitorFilter): void {
    Object.entries(monitorFilter)
      .map(([groupId, value]) => getFilterAction(groupId, value))
      .forEach(action => this.store.dispatch(action));
  }

  public resetViewTypes(): void {
    this.store.dispatch(new SetGamedayViewType(GameDayViewType.PriorityType));
    this.store.dispatch(new SetMonitorViewType(MonitorViewType.Overview));
  }

  public getTranche(): Observable<Tranche> {
    return combineLatest(
      this.viewType.pipe(
        switchMap(viewType => this.dataMapByViewType[viewType])
      ),
      this.viewType,
      this.selectedMaturityId
    ).pipe(
      map(
        ([data, viewType, id]: [
          GameDayCalculatedData,
          MonitorViewType,
          string
        ]) => {
          const gameDayViewType =
            MONITOR_TO_GAME_DAY_VIEW_TYPE_MAPPING[viewType];
          return filteredDataToTranche(data, gameDayViewType, id);
        }
      )
    );
  }
}
